import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

// Shapes mirror the get_b2b_billing_statement / get_b2b_billing_months RPC
// payloads (money math happens server-side; render these verbatim so the
// portal always matches what admin bills).

export interface StatementLineItem {
  session_id: string;
  session_type: 'interview' | 'online_assessment' | string;
  end_client_name: string;
  position_title: string | null;
  company_name: string | null;
  interview_date: string | null;
  billed_on: string;
  is_billable: boolean;
  waived: boolean;
  waive_reason: string | null;
  unit_price: number;
  amount: number;
}

export interface StatementPricing {
  interview_price: number;
  assessment_price: number;
  currency: string;
  volume_tiers: { min_orders: number; discount_pct: number }[];
  is_default_pricing: boolean;
}

export interface StatementSummary {
  interview_count: number;
  assessment_count: number;
  billable_interviews: number;
  billable_assessments: number;
  billable_count: number;
  waived_count: number;
  subtotal: number;
  discount_pct: number;
  discount_amount: number;
  total: number;
}

export interface BillingStatement {
  month: string;
  line_items: StatementLineItem[];
  pricing: StatementPricing;
  summary: StatementSummary;
}

export interface BillingMonth {
  month: string;
  setup_count: number;
  billable_count: number;
  waived_count: number;
  subtotal: number;
  discount_pct: number;
  total: number;
  currency: string;
}

async function fetchBillingMonths(companyId: string): Promise<BillingMonth[]> {
  const { data, error } = await supabase.rpc(
    'get_b2b_billing_months' as never,
    {
      p_company_id: companyId,
    } as never
  );
  if (error) throw new Error(`get_b2b_billing_months: ${error.message}`);
  return ((data as unknown) ?? []) as BillingMonth[];
}

async function fetchBillingStatement(companyId: string, month: string): Promise<BillingStatement> {
  const { data, error } = await supabase.rpc(
    'get_b2b_billing_statement' as never,
    {
      p_company_id: companyId,
      p_month: month,
    } as never
  );
  if (error) throw new Error(`get_b2b_billing_statement: ${error.message}`);
  return data as unknown as BillingStatement;
}

/** Months with any interview/assessment setup, newest first, with totals. */
export function useBillingMonths() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'billing-months', companyId],
    queryFn: () => fetchBillingMonths(companyId),
    staleTime: 60_000,
  });
}

/** Itemized statement for one month ('YYYY-MM-01'). Disabled until a month is chosen. */
export function useBillingStatement(month: string | null) {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'billing-statement', companyId, month],
    queryFn: () => fetchBillingStatement(companyId, month as string),
    enabled: !!month,
    staleTime: 60_000,
  });
}
